import { jsPDF } from "jspdf";
import "jspdf-autotable";
import moment from "@/utils/moment";
import * as XLSX from "xlsx";

export function download(filename, data, type) {
  const blob = new Blob([data], { type });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
}

/**
 * Generate an excel file browser side
 *
 * @param filename
 * @param worksheet
 * @param cols
 * @param rows
 */
export function generateExcel(filename, worksheet, cols, rows) {
  const ws = XLSX.utils.aoa_to_sheet([cols, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, worksheet);

  // Function to calculate the maximum width for each column
  const getMaxWidth = (sheet) => {
    const colWidths = [];
    const range = XLSX.utils.decode_range(sheet["!ref"]);

    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10; // Minimum column width
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = { c: C, r: R };
        const cell = sheet[XLSX.utils.encode_cell(cellAddress)];
        if (cell && cell.v) {
          const cellValue = cell.v.toString();
          const cellWidth = cellValue.length + 5;
          if (cellWidth > maxWidth) {
            maxWidth = cellWidth;
          }
        }
      }
      colWidths.push({ wch: maxWidth });
    }
    return colWidths;
  };

  // Set the column widths
  ws["!cols"] = getMaxWidth(ws);

  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Generate a PDF file
 *
 * @param filename
 * @param meta
 * @param cols
 * @param rows
 * @param orientation
 */
export function generatePDF(
  filename,
  {
    title,
    centralOrWorkgroup,
    description,
    operator,
    introColumnStyles = {},
    introTitle = "Resumen",
    introHeaders = [],
    introData = [],
    overflow = "linebreak",
    columnStyles = {},
    breakPageAfterIntro = false,
    showCount = true,
    chartsAfterIntro = null,
    breakPageAfterCharts = false,
    logo,
  },
  cols,
  rows,
  orientation = "landscape",
  multi = false
) {
  const date = moment()().format("LLL");
  const doc = new jsPDF({ putOnlyUsedFonts: true, orientation });
  doc.addPage("a4", "portrait");
  doc.deletePage(1);
  doc.setFontSize(24);
  doc.text(title || "", 105, 90, { align: "center" });
  doc.setFontSize(10);
  doc.setTextColor("#777777");
  doc.text(description || "", 105, 100, { align: "center" });
  doc.setTextColor("#000000");
  doc.setFontSize(16);
  doc.text(centralOrWorkgroup || "", 105, 120, { align: "center" });
  doc.setFontSize(11);
  doc.text(`Generado a ${date}`, 105, 128, { align: "center" });
  doc.setFontSize(11);
  doc.text(`Exportado por ${operator}`, 105, 134, { align: "center" });
  doc.addImage(logo, "PNG", 90, 230, 30, 30);
  doc.addPage("a4", orientation);
  let startY = 15;
  if (introHeaders && introHeaders.length > 0 && introData.length > 0) {
    const titleY = 15;
    const tableStart = titleY + 3;
    doc.setFontSize(16);
    doc.text(introTitle, 10, titleY);
    doc.setFontSize(11);
    doc.autoTable({
      startY: tableStart,
      head: [introHeaders],
      body: introData,
      margin: { top: 8, left: 8, right: 8, bottom: 8 },
      styles: { fontSize: 7, cellPadding: 1, overflow },
      headStyles: { overflow: "hidden" },
      introColumnStyles,
    });
    if (breakPageAfterIntro) {
      doc.addPage();
      startY = 15;
    } else {
      startY = doc.autoTable.previous.finalY + 20;
    }
  }
  if (chartsAfterIntro) {
    startY -= 15;
    chartsAfterIntro.forEach((chartRow) => {
      const imageWidth = 280 / chartRow.length;
      const imageHeight = 60;
      chartRow.forEach((chart, i) => {
        const info = doc.getImageProperties(chart);
        const aspectRatio = info.width / info.height;
        const newWidth = imageHeight * aspectRatio;
        const leftPadding = (imageWidth - newWidth) / 2;
        doc.addImage(chart, "PNG", 10 + leftPadding + imageWidth * i, startY, newWidth, 60);
      });
      startY += 64;
    });
    startY += 5;
    if (breakPageAfterCharts) {
      doc.addPage();
      startY = 15;
    }
  }
  if (multi) {
    rows.forEach((row) => {
      if (row.breakPageBefore) {
        doc.addPage("a4", row.orientation || orientation);
        startY = 15;
      }
      const titleY = startY;
      const tableStart = titleY + 3;
      doc.setFontSize(16);
      doc.text(row.title, 10, titleY);
      if (showCount) {
        doc.setFontSize(11);
        doc.text(`${row.rows.length}`, 288, titleY, { align: "right" });
      }
      doc.autoTable({
        startY: tableStart,
        head: [row.cols || cols],
        body: row.rows,
        margin: { top: 8, left: 8, right: 8, bottom: 8 },
        styles: { fontSize: 7, cellPadding: 1, overflow },
        headStyles: { overflow: "hidden" },
        columnStyles,
      });
      startY = doc.autoTable.previous.finalY + 10;
    });
  } else {
    doc.autoTable({
      head: [cols],
      body: rows,
      margin: { top: 8, left: 8, right: 8, bottom: 8 },
      styles: { fontSize: 7, cellPadding: 1, overflow },
      headStyles: { overflow: "hidden" },
      columnStyles,
    });
  }
  doc.save(`${filename}.pdf`);
}

/**
 * Generate KML file from a list of folders that contains a list of points
 *
 * @param filename
 * @param title
 * @param folders
 */
export function generateKML(filename, title, folders) {
  const parsedFolder = folders
    .map((folder, i) => {
      const visibility = i === 0 ? 1 : 0;
      const parsedPoints = folder.points.map((point) => {
        return `      <Placemark>
        <name>${point.name}</name>
        <visibility>${visibility}</visibility>
        <Point>
          <coordinates>${point.coordinates.join(",")}</coordinates>
        </Point>
      </Placemark>`;
      });
      return `    <Folder>
      <visibility>${visibility}</visibility>
      <name>${folder.name}</name>
      ${parsedPoints.join("\r\n")}
    </Folder>`;
    })
    .join("");

  const data = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${title}</name>
    ${parsedFolder}
  </Document>
</kml>`;

  download(filename, data, "application/vnd.google-earth.kml+xml");
}

/**
 * Generate KML file from a list of folders that contains a list of polygons
 *
 * @param filename
 * @param title
 * @param folders
 */
export function generateKMLPolygon(filename, title, folders) {
  const parsedFolder = folders
    .map((folder) => {
      const parsedPolygons = folder.polygons.map((polygon) => {
        return `
      <Placemark>
        <name>${polygon.name}</name>
        <visibility>1</visibility>
        <Polygon>
          <tessellate>1</tessellate>
          <altitudeMode>relative</altitudeMode>
          <outerBoundaryIs>
            <LinearRing>
              <coordinates>${polygon.coordinates.join(",100\r\n")}</coordinates>
            </LinearRing>
          </outerBoundaryIs>
        </Polygon>
      </Placemark>`;
      });
      return `<Folder>
      <visibility>1</visibility>
      <name>${folder.name}</name>
      ${parsedPolygons.join("\r\n")}
    </Folder>`;
    })
    .join("");

  const data = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${title}</name>
    ${parsedFolder}
  </Document>
</kml>`;

  download(filename, data, "application/vnd.google-earth.kml+xml");
}
